/**
 * What the composer's skill picker lists on a thread: the firm's active
 * skills that target the thread's focus, then the built-in procedures the
 * assistant ships for it. A firm skill without a `focuses` list (an older
 * payload) is shown everywhere, as before. A firm skill that replaces a
 * built-in (`replaces`, or the same slug) stands in for it: the built-in is
 * left out and the firm skill carries its title for the "Replaces built-in"
 * note. Hidden skills stay in the list (a thread may use one) but are not
 * offered (`isOffered`). On a case, a firm skill scoped to other visas
 * (`visas`) is left out.
 */
import type { FirmSkillSummary } from "@/features/firmKnowledge/types";
import { canonicalVisa } from "@/features/firmKnowledge/visaCatalog";

import type { BuiltinSkillSummary, ScopeKind } from "./types";

export const isBuiltinSkillId = (id: string | null | undefined): boolean =>
  typeof id === "string" && id.startsWith("builtin:");

/** A built-in skill in the shape the picker and the slash menu take. */
export const builtinAsSummary = (
  skill: BuiltinSkillSummary
): FirmSkillSummary => ({
  id: skill.id,
  slug: skill.slug,
  title: skill.title,
  description: skill.description,
  status: "active",
  inputs: [],
  default_mode: "agent",
  focuses: skill.focuses,
  builtin: true
});

/** Whether the picker and the slash menu offer the skill. */
export const isOffered = (skill: FirmSkillSummary): boolean =>
  skill.visibility !== "hidden";

const visaKey = (visa: string): string =>
  (canonicalVisa(visa) ?? visa).trim().toLowerCase();

/** Whether a skill is for a case of `visa`: unscoped skills are for every case. */
export const fitsVisa = (
  skill: Pick<FirmSkillSummary, "visas">,
  visa: string | null | undefined
): boolean =>
  !visa ||
  !skill.visas?.length ||
  skill.visas.some((v) => visaKey(v) === visaKey(visa));

export const skillsForFocus = (
  firmSkills: readonly FirmSkillSummary[],
  builtinSkills: readonly BuiltinSkillSummary[] | undefined,
  focus: ScopeKind | null | undefined,
  visa?: string | null
): FirmSkillSummary[] => {
  const builtin = (builtinSkills ?? []).filter(
    (s) => !focus || s.focuses.includes(focus)
  );
  const replaced = new Set<string>();
  const firm = firmSkills
    .filter((s) => !focus || !s.focuses?.length || s.focuses.includes(focus))
    .filter((s) => fitsVisa(s, visa))
    .map((s) => {
      const stands = builtin.find(
        (b) => b.slug === s.replaces || b.slug === s.slug
      );
      if (!stands || !isOffered(s)) return s;
      replaced.add(stands.id);
      return { ...s, replaces_title: stands.title };
    });
  return [
    ...firm,
    ...builtin.filter((b) => !replaced.has(b.id)).map(builtinAsSummary)
  ];
};
